/**
 * @file capture/systemAudio.ts
 * @brief The other side of a Zoom / Teams / Meet call, in the desktop app only.
 *
 * @details
 * A browser hears the mic and nothing else. With headphones on, the remote side
 * of a call never reaches that mic, and on speaker it arrives as a quiet room
 * echo. The Verblizr desktop app (DESKTOP/, Electron) answers
 * `getDisplayMedia()` with the machine's system audio instead of a screen
 * picker, so the remote side is recorded as the clean digital signal that is
 * about to go to the speakers or headphones.
 *
 * In a normal browser tab `window.verblizrDesktop` is absent, the option is
 * never offered, and nothing here runs: the webpanel recorder is unchanged.
 *
 * @author DES
 * @date 30-09-2026
 */

import { desktopShell } from "@/shared/desktop/desktopShell";

/** True only inside the desktop app, where call audio can be captured. */
export function canCaptureSystemAudio(): boolean {
  return (
    !!desktopShell()?.systemAudio &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function"
  );
}

/**
 * Open the system-audio stream. Resolves to an audio-only stream, or throws an
 * Error whose message is ready to show the user.
 *
 * getDisplayMedia insists on a video track; the desktop shell hands back its own
 * window for it, and it is stopped here before anything can record it.
 */
export async function openSystemAudio(
  opts: {
    /**
     * Leave out what this app itself is playing (the Interpreter's
     * translations), so they are never captured and translated again.
     * Chromium's `restrictOwnAudio`; measured in the desktop app on
     * 02-10-2026: our own tone 0.213 → 0, another app's sound still in
     * (docs/product/desktop-call-interpretation-plan.md, Phase 0).
     */
    excludeOwnAudio?: boolean;
  } = {},
): Promise<MediaStream> {
  let display: MediaStream;
  try {
    display = await navigator.mediaDevices.getDisplayMedia({
      audio: opts.excludeOwnAudio
        ? ({ restrictOwnAudio: true, suppressLocalAudioPlayback: false } as MediaTrackConstraints)
        : true,
      video: true,
    });
  } catch {
    throw new Error(
      "Could not capture call audio. Allow Verblizr under System Settings → Privacy & Security → Screen & System Audio Recording, or turn call audio off to record the mic only.",
    );
  }
  display.getVideoTracks().forEach((t) => t.stop());
  const audio = display.getAudioTracks();
  if (audio.length === 0) {
    throw new Error(
      "This Mac did not share any call audio. Turn call audio off to record the mic only.",
    );
  }
  return new MediaStream(audio);
}
